import { RegisterScreen } from '@/components/RegisterScreen';
import { formatNumber } from '@/components/live';
import { usePickers } from '@/hooks/usePickers';

interface NormRow {
  id: string; species_id: string | null; species_label: string | null; thickness_in: number | null; is_mixed: boolean;
  expected_days: number; target_awm_pct: number; target_temp_c: number | null; source_note: string | null;
}

/**
 * How long a seasoning cycle should take, by species and board thickness. It plans a charge's end
 * date. Keyed on the species REFERENCE, never a typed name: the source workbook said "Acacia" in its
 * norms and "Babool" on every stack, so a lookup by name found nothing. A mixed load is a flag, not a
 * thickness, because a charge dries at the pace of its thickest board.
 */
export default function ChamberNormsScreen() {
  const pick = usePickers(['species']);
  return (
    <RegisterScreen<NormRow>
      addLabel="Add norm" exportName="seasoning-norms" recordLabel={(row) => `norm ${row.species_label ?? 'any species'} ${row.is_mixed ? 'mixed' : `${row.thickness_in}"`}`}
      recordNoun="norm"
      screenKey="site.register" searchColumns={['species_label', 'source_note']}
      subtitle="Expected cycle length by species and board thickness, with the moisture and temperature targets a charge is dried to."
      table="ops.chamber_duration_norms" title="Seasoning norms" view="chamber_duration_norms" orderBy={{ column: 'species_label', ascending: true }}
      columns={[
        { key: 'species_label', label: 'Species', render: (row) => <strong>{row.species_label ?? 'Any species'}</strong> },
        { key: 'thickness_in', label: 'Thickness', render: (row) => (row.is_mixed ? 'Mixed load' : `${formatNumber(row.thickness_in, 2)} in`) },
        { key: 'expected_days', label: 'Expected days', align: 'right', render: (row) => formatNumber(row.expected_days, 1) },
        { key: 'target_awm_pct', label: 'Target moisture', align: 'right', render: (row) => `${formatNumber(row.target_awm_pct, 1)}%` },
        { key: 'target_temp_c', label: 'Target temp.', align: 'right', render: (row) => (row.target_temp_c ? `${formatNumber(row.target_temp_c, 0)} °C` : '--') },
        { key: 'source_note', label: 'Source', render: (row) => <span className="muted">{row.source_note ?? '--'}</span> },
      ]}
      stats={[
        { key: 'mixed', label: 'Mixed loads', tone: 'info', match: (row) => row.is_mixed },
        { key: 'long', label: 'Over 15 days', tone: 'warn', match: (row) => row.expected_days > 15 },
      ]}
      defaults={{ is_mixed: false, target_awm_pct: '6.5' }}
      fields={[
        { key: 'species_id', label: 'Species', type: 'select', options: pick.species, required: true,
          hint: 'From the species reference. A species missing there (e.g. Babool) has to be added to it first.' },
        { key: 'is_mixed', label: 'Mixed load', type: 'check', placeholder: 'A mixed charge - planned from its thickest board' },
        { key: 'thickness_in', label: 'Thickness (inches)', type: 'number', required: true, showIf: (form) => !form.is_mixed },
        { key: 'expected_days', label: 'Expected days', type: 'number', required: true, hint: 'Up to 120.' },
        { key: 'target_awm_pct', label: 'Target moisture (%)', type: 'number', required: true, hint: 'Average wood moisture, 4 to 20. 6.5% is the usual target.' },
        { key: 'target_temp_c', label: 'Target temperature (°C)', type: 'number', hint: '60 to 90.' },
        { key: 'source_note', label: 'Source', type: 'text', placeholder: 'e.g. Median of 38 emptied runs, Unit A, 2025' },
      ]}
      validate={(form) => {
        const n = (key: string) => (form[key] === '' || form[key] === null || form[key] === undefined ? null : Number(form[key]));
        const days = n('expected_days'); const awm = n('target_awm_pct'); const temp = n('target_temp_c'); const thick = n('thickness_in');
        return {
          expected_days: days !== null && (days <= 0 || days > 120) ? 'Between 0 and 120 days.' : undefined,
          target_awm_pct: awm !== null && (awm < 4 || awm > 20) ? 'Between 4 and 20%.' : undefined,
          target_temp_c: temp !== null && (temp < 60 || temp > 90) ? 'Between 60 and 90 °C.' : undefined,
          thickness_in: !form.is_mixed && thick !== null && thick <= 0 ? 'Must be more than zero.' : undefined,
        };
      }}
    />
  );
}
